import i18n from 'i18next';
import { data, dataHora, dinheiro, nomePessoa, numero } from './formato';
import { rotuloEtapaPipeline, rotuloTextoLivre, rotuloValor } from './rotulos';
import { descricaoGerada } from './texto-sistema';

/** Campos estruturados de um vínculo (espelho de `VinculoEstruturado` em `modules/cadastros/src/publico.ts`). */
export interface VinculoEstruturado {
  tipo: string;
  subtipo?: string | null;
  numero?: string | null;
  titulo?: string | null;
  titular?: string | null;
  com?: string | null;
  estado?: string | null;
  etapa?: string | null;
  valor_centavos?: number | null;
  moeda?: string | null;
  data?: string | null;
  quantidade?: number | null;
  unidade?: string | null;
}

export interface ItemVinculo {
  id: string;
  rotulo: string;
  detalhe?: string | null;
  link: string;
  estruturado?: VinculoEstruturado;
}

const t = (chave: string, opcoes?: Record<string, unknown>) => i18n.t(chave, opcoes);
/** Tradução do módulo dono do código, ou o rótulo genérico do núcleo (nunca o código cru). */
const traduzir = (chave: string, campo: string, valor: string) => (i18n.exists(chave) ? t(chave) : rotuloValor(campo, valor));

const ESTADO: Record<string, string> = { contas: 'contas:estado', compras: 'compras:estadoPedido', agenda: 'agenda:estados', conversas: 'conversas:estados' };
const CANAL: Record<string, string> = { whatsapp: 'WhatsApp', instagram: 'Instagram', portal: 'Portal' };

function quando(v: string | null | undefined) {
  if (!v) return '';
  return v.length > 10 ? dataHora(v) : data(v);
}

/**
 * Texto do vínculo na ficha do parceiro, no idioma e na moeda do app (review r6 visual M4). Com os campos estruturados, cada
 * parte é montada aqui (tipo e estado traduzidos, dinheiro com `dinheiro()`, data no formato do idioma); texto do usuário
 * (título, etapa, nome) aparece como foi gravado. Servidor antigo, sem `estruturado`: cai no texto pronto, com datas e palavras
 * conhecidas traduzidas por `rotuloTextoLivre`.
 */
export function textoVinculo(modulo: string, item: ItemVinculo): { rotulo: string; detalhe: string } {
  const e = item.estruturado;
  if (!e) return { rotulo: rotuloTextoLivre(item.rotulo), detalhe: rotuloTextoLivre(item.detalhe) };
  const estado = e.estado ? traduzir(`${ESTADO[modulo] ?? `${modulo}:estado`}.${e.estado}`, 'estado', e.estado) : '';
  const valor = e.valor_centavos === null || e.valor_centavos === undefined ? '' : dinheiro(e.valor_centavos, e.moeda ?? undefined);
  const juntar = (...partes: (string | null | undefined)[]) => partes.filter((p) => p && p.trim()).join(' · ');
  switch (e.tipo) {
    case 'fatura':
    case 'conta_pagar':
    case 'nota_credito':
    case 'reembolso':
      return { rotulo: `${traduzir(`contas:tipo.${e.tipo}`, 'tipo', e.tipo)} ${e.numero ?? ''}`.trim(), detalhe: juntar(quando(e.data), valor, estado) };
    case 'pedido_compra':
      return { rotulo: t('vinculo.pedido', { numero: e.numero ?? '' }).trim(), detalhe: juntar(estado, valor) };
    case 'oportunidade':
      // Lista de 03/11, item 3: a negociação em que o parceiro é empresa adicional (a principal é de outro cliente) ou pessoa de contato.
      return { rotulo: e.titulo ?? item.rotulo, detalhe: juntar(e.subtipo === 'adicional' ? t('vinculo.adicional') : e.subtipo === 'contato' ? t('vinculo.contato') : '', rotuloEtapaPipeline(e.etapa), valor) };
    case 'agendamento':
      return { rotulo: e.com ? t('vinculo.com', { titulo: e.titulo ?? '', com: nomePessoa(e.com) }) : (e.titulo ?? item.rotulo), detalhe: juntar(quando(e.data), estado) };
    case 'lancamento': {
      const sinal = e.subtipo === 'entrada' ? '+' : e.subtipo === 'saida' ? '-' : '';
      return { rotulo: descricaoGerada(e.titulo ?? item.rotulo), detalhe: juntar(quando(e.data), valor && `${sinal}${valor}`) };
    }
    case 'conversa':
      return { rotulo: `${CANAL[e.subtipo ?? ''] ?? e.subtipo ?? ''}: ${e.titulo ?? t('vinculo.semMensagens')}`, detalhe: juntar(estado, quando(e.data)) };
    // Fase 2 (W23, K10): o funcionário ligado ao parceiro (Pessoas); nome pelas iniciais com a opção de nomes.
    case 'funcionario':
      return { rotulo: nomePessoa(e.titulo ?? item.rotulo), detalhe: estado };
    case 'produto':
      return {
        rotulo: `${e.numero ?? ''} ${e.titulo ?? ''}`.trim() || item.rotulo,
        detalhe: e.quantidade === null || e.quantidade === undefined ? '' : t('vinculo.saldo', { quantidade: numero(e.quantidade, Number.isInteger(e.quantidade) ? 0 : 2), unidade: e.unidade ?? '' }).trim(),
      };
    // Pacote 2 (Projetos e tarefas): o quadro do projeto do cliente e a tarefa ligada a ele.
    case 'projeto_tarefas':
      return {
        rotulo: t('tarefas:vinculo.projeto', { titulo: e.titulo ?? '' }),
        detalhe: e.estado === 'arquivado' ? t('tarefas:projetos.arquivado') : t('tarefas:projetos.abertas', { count: e.quantidade ?? 0, n: numero(e.quantidade ?? 0) }),
      };
    case 'tarefa':
      return { rotulo: `${e.numero ?? ''} ${e.titulo ?? ''}`.trim() || item.rotulo, detalhe: juntar(quando(e.data), e.estado ? t(`tarefas:vinculo.${e.estado}`) : '') };
    default:
      return { rotulo: e.titulo ?? rotuloTextoLivre(item.rotulo), detalhe: juntar(quando(e.data), valor, estado) || rotuloTextoLivre(item.detalhe) };
  }
}
